/**
 * Admin Users Hooks
 * ადმინისტრატორის მომხმარებლების ჰუკები
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdminUsers,
  getAdminUserDetails,
  changeUserPlan,
  applyUserDiscount,
  suspendUser,
  activateUser,
  createCustomInvoice,
  getAdminUserAnalyticsOverview,
  getAdminUserAnalyticsTraffic,
  type AdminUsersParams,
  type ChangeUserPlanRequest,
  type ApplyDiscountRequest,
  type SuspendUserRequest,
  type CreateCustomInvoiceRequest,
} from '../features/api/adminUsers';
import type { StatisticsPeriod } from '../features/api/analytics';

export function useAdminUsers(params: AdminUsersParams = {}) {
  const query = useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => getAdminUsers(params),
    staleTime: 1000 * 60, // 1 minute
  });

  return {
    users: query.data?.content ?? [],
    totalElements: query.data?.totalElements ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAdminUserDetails(userId: string | null) {
  const query = useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: () => getAdminUserDetails(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60,
  });

  return {
    user: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useChangeUserPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: ChangeUserPlanRequest }) =>
      changeUserPlan(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}

export function useApplyUserDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: ApplyDiscountRequest }) =>
      applyUserDiscount(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}

export function useSuspendUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: SuspendUserRequest }) =>
      suspendUser(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => activateUser(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
}

export function useCreateCustomInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateCustomInvoiceRequest }) =>
      createCustomInvoice(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}

export function useAdminUserAnalyticsOverview(userId: string | null) {
  const query = useQuery({
    queryKey: ['admin', 'users', userId, 'analytics', 'overview'],
    queryFn: () => getAdminUserAnalyticsOverview(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });

  return {
    overview: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useAdminUserAnalyticsTraffic(userId: string | null, period: StatisticsPeriod = 'MONTH') {
  const query = useQuery({
    queryKey: ['admin', 'users', userId, 'analytics', 'traffic', period],
    queryFn: () => getAdminUserAnalyticsTraffic(userId!, period),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });

  return {
    traffic: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
